import { Stack } from '@mui/material';
import { TPerson, TValidationResults } from '@verifime/api-definition';
import {
  DisplayErrors,
  GenerateFormFields_New,
  generateNonFieldErrors,
  getFieldsRules_New,
  showFieldsErrors,
  TErrors,
  useCustomForm,
} from '@verifime/components';
import tokens from '@verifime/design-tokens';
import { SupportedCountryCode, validationRules } from '@verifime/utils';
import cloneDeep from 'lodash/cloneDeep';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { driverLicenceFormFields, generateFormFieldsAndRules } from './formFieldConfigs';
import {
  buildOcrData,
  checkIsEmptyForm,
  filterDocumentData,
  TOcrData,
  TSupportedOcrDocumentType,
} from './utils';

export const OcrDocumentEditor = ({
  customer,
  initialData,
  documentType,
  structuredNameRequired,
  onFormChange,
  validationResults,
}: Readonly<{
  customer: TPerson;
  initialData: TOcrData;
  documentType: TSupportedOcrDocumentType;
  structuredNameRequired: boolean;
  onFormChange?: (isValid: boolean, data: TOcrData) => void;
  validationResults: TValidationResults;
}>) => {
  const formFieldsAndRules = useMemo(
    () =>
      generateFormFieldsAndRulesWithLogic(
        customer.address?.countryCode,
        documentType,
        structuredNameRequired,
      ),
    [documentType, customer.address?.countryCode, structuredNameRequired],
  );
  const documentData = useMemo(
    () => filterDocumentData(initialData, documentType, formFieldsAndRules),
    [initialData, documentType, formFieldsAndRules],
  );

  const methods = useCustomForm<Record<string, any>>({
    defaultValues: documentData || {},
    schema: getFieldsRules_New(formFieldsAndRules),
  });
  const { setValue, getValues, trigger, watch, updateSchema, setError } = methods;

  const isMounted = useRef(true);

  const [nonFieldErrors, setNonFieldErrors] = useState<TErrors>([]);

  useEffect(() => {
    if (validationResults?.length < 1) {
      return;
    }
    showFieldsErrors(validationResults, setError);
    const nonFieldErrors = generateNonFieldErrors(validationResults);
    setNonFieldErrors(nonFieldErrors);
  }, [validationResults, setError]);

  useEffect(() => {
    updateSchema(getFieldsRules_New(formFieldsAndRules), { resetSchema: true });
  }, [formFieldsAndRules, updateSchema]);

  useEffect(() => {
    if (Object.keys(documentData || {}).length < 1) {
      return;
    }
    Object.entries(documentData).forEach(([k, v]) => setValue(k, v));
  }, [documentData, setValue]);

  useEffect(() => {
    if (!isMounted.current) {
      return;
    }
    const checkInitial = async () => {
      const values = getValues();
      const isEmptyForm = checkIsEmptyForm(values);
      let valid = false;
      if (!isEmptyForm) {
        valid = await trigger();
      }
      onFormChange?.(valid, buildOcrData(documentType, values));
    };
    checkInitial();
    return () => {
      isMounted.current = false;
    };
  }, [getValues, trigger, onFormChange, documentType]);

  // Watch for subsequent changes
  useEffect(() => {
    const subscription = watch(async (formData) => {
      // Only trigger validation and call onFormChange if form data actually changes
      const isEmptyForm = checkIsEmptyForm(formData);
      let valid = false;
      if (!isEmptyForm) {
        valid = await trigger();
      }
      onFormChange?.(valid, buildOcrData(documentType, formData));
    });
    return () => subscription.unsubscribe();
  }, [watch, trigger, onFormChange, documentType]);

  return (
    <FormProvider {...methods}>
      <Stack mt={tokens.spacingLg}>
        <GenerateFormFields_New
          formFieldsAndRules={formFieldsAndRules}
          control={methods.control}
          errors={methods.formState.errors}
          formFieldsWithDefaultValues={documentData}
        />
        <DisplayErrors errors={nonFieldErrors} />
      </Stack>
    </FormProvider>
  );
};

// This is a temporary solution, ideally we no need this logic.
// That means that once we render form according to the selected country,
// then form validaiton can be handled in specific document form editor natually.
function generateFormFieldsAndRulesWithLogic(
  countryCode: string,
  documentType: TSupportedOcrDocumentType,
  structuredNameRequired: boolean,
) {
  const result = generateFormFieldsAndRules(documentType, structuredNameRequired);

  if (documentType === 'DriverLicence') {
    if (countryCode === SupportedCountryCode.AU || countryCode === SupportedCountryCode.AUS) {
      const licenceCardNumber = driverLicenceFormFields.licenceCardNumber.fieldName;

      const formFieldsAndRules = cloneDeep(result);
      Object.values(formFieldsAndRules).forEach((fieldAndRules) => {
        fieldAndRules.forEach((fieldAndRule) => {
          if (fieldAndRule.fieldName === licenceCardNumber) {
            fieldAndRule.rules =
              validationRules.REQUIRED_DRIVER_LICENCE_CARD_NUMBER_VALIDATION_RULES;
          }
        });
      });
      return formFieldsAndRules;
    }
  }

  return result;
}
